import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FolderKanban, Clock, ArrowRight, FileText, MoreHorizontal } from 'lucide-react';
import api from '../../services/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../common/Toast';

export default function DprExistingList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    fetchProjects();

    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/sourcing/project-requirements');
      if (res.data.success) {
        // Only show projects that haven't been moved to Pre-RFP yet
        const dprProjects = res.data.data.filter(p => p.dprStatus !== 'Moved to Pre-RFP');
        setProjects(dprProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      showToast('Failed to load projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(p => 
    p.projectTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.category && p.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <PageBreadcrumb 
        paths={[
          { label: 'Sourcing', url: '/sourcing' },
          { label: 'Requirement Gathering', url: '/sourcing/requirement-gathering' },
          { label: 'DPR', url: '/sourcing/dpr' },
          { label: 'Existing', url: '/sourcing/dpr/existing' }
        ]} 
      />
      <PageHeader 
        title="Existing DPRs" 
        subtitle="Select a project requirement to draft its Detailed Project Report." 
      />
      <div className="page-container container-fluid px-4">

        <div className="card border-0 shadow-sm pr-card mt-4">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="position-relative" style={{ width: '300px' }}>
                <Search size={18} className="position-absolute text-muted" style={{ left: '12px', top: '10px' }} />
                <input
                  type="text"
                  className="pr-input"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '36px', width: '100%' }}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FileText size={48} className="mb-3 opacity-50" />
                <h5>No Projects Available for DPR</h5>
                <p>Complete a requirement gathering phase first to start a DPR.</p>
              </div>
            ) : (
              <div style={{ minHeight: '300px' }}>
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Project Title</th>
                      <th>Category</th>
                      <th>Req Status</th>
                      <th>DPR Status</th>
                      <th>Owner</th>
                      <th>Created At</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map(project => {
                      const dprStatus = project.dprStatus || 'Not Initiated';
                      
                      return (
                        <tr key={project._id}>
                          <td>
                            <div className="fw-medium text-primary d-flex align-items-center gap-2">
                              <FolderKanban size={16} />
                              {project.projectTitle}
                            </div>
                          </td>
                          <td>{project.category || 'N/A'}</td>
                          <td>
                            <span className={`badge ${
                              project.status === 'Draft' ? 'bg-secondary' : 
                              project.status === 'In Collaboration' ? 'bg-warning text-dark' : 'bg-success'
                            }`}>
                              {project.status}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              dprStatus === 'Not Initiated' ? 'bg-secondary' : 
                              dprStatus === 'In Progress' ? 'bg-primary' : 'bg-success'
                            }`}>
                              {dprStatus}
                            </span>
                          </td>
                          <td>{project.owner ? `${project.owner.firstName || ''} ${project.owner.lastName || ''}`.trim() : 'Unknown'}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1 text-muted small">
                              <Clock size={14} />
                              {new Date(project.createdAt).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="text-end">
                            <div className="dropdown position-relative">
                              <button 
                                className="btn btn-light btn-sm rounded-circle p-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(activeDropdown === project._id ? null : project._id);
                                }}
                              >
                                <MoreHorizontal size={16} className="text-muted" />
                              </button>
                              {activeDropdown === project._id && (
                                <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0 show" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1050 }}>
                                  <li>
                                    <button 
                                      className="dropdown-item d-flex align-items-center gap-2 py-2"
                                      onClick={() => navigate(`/sourcing/dpr/${project._id}`)}
                                    >
                                      <FileText size={14} className="text-primary" /> 
                                      {dprStatus === 'Not Initiated' ? 'Initiate DPR' : 'Manage DPR'}
                                    </button>
                                  </li>
                                </ul>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
